<script setup lang="ts">
import AppIcon from '@/components/ui/AppIcon.vue'
import type { IconName } from '@/lib/icons'
import { useUiStore } from '@/stores/ui'

const ui = useUiStore()

const STYLES = {
  success: 'bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950 dark:text-emerald-200',
  error: 'bg-red-50 text-red-800 ring-red-200 dark:bg-red-950 dark:text-red-200',
  warning: 'bg-amber-50 text-amber-900 ring-amber-200 dark:bg-amber-950 dark:text-amber-200',
  info: 'bg-white text-gray-800 ring-gray-200 dark:bg-neutral-800 dark:text-neutral-200',
} as const

const ICONS: Record<keyof typeof STYLES, IconName> = {
  success: 'circle-check',
  error: 'circle-alert',
  warning: 'triangle-alert',
  info: 'info',
}
</script>

<template>
  <Teleport to="body">
    <!-- Polite, so a toast does not interrupt a screen reader mid-sentence. -->
    <div
      class="pointer-events-none fixed bottom-0 end-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4"
      role="status"
      aria-live="polite"
    >
      <TransitionGroup
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="translate-y-2 opacity-0"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-for="toast in ui.toasts"
          :key="toast.id"
          class="pointer-events-auto flex w-full max-w-sm items-start gap-x-3 rounded-lg px-4 py-3 text-sm shadow-lg ring-1"
          :class="STYLES[toast.variant]"
        >
          <AppIcon :name="ICONS[toast.variant]" size="sm" class="mt-0.5" />
          <span class="flex-1">{{ toast.message }}</span>
          <button
            type="button"
            class="shrink-0 rounded opacity-60 hover:opacity-100"
            @click="ui.dismiss(toast.id)"
          >
            <span class="sr-only">Dismiss</span>
            <AppIcon name="x" size="sm" />
          </button>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>
